import type { CSSProperties } from 'react';
import DarkModeToggle from './components/DarkModeToggle';
import ContentsRail from './components/ContentsRail';
import MobileNav from './components/MobileNav';
import QuickLinksSection from './sections/QuickLinksSection';
import IntroductionSection from './sections/IntroductionSection';
import SkillsSection from './sections/SkillsSection';
import ProjectsSection from './sections/ProjectsSection';
import ContactSection from './sections/ContactSection';
import { useDarkMode } from './hooks/useDarkMode';
import { useIsMobile, useIsNarrow } from './hooks/useIsMobile';
import { useScrollSpy } from './hooks/useScrollSpy';
import { getColors } from './theme';
import { NAV_ITEMS } from './data';

const SECTION_IDS = NAV_ITEMS.map(n => n.id);

// Stashed — flip to true to bring the projects section back (also add it to NAV_ITEMS).
const SHOW_PROJECTS = false;

export default function ProfileCard() {
  const [dark, setDark] = useDarkMode();
  const mobile = useIsMobile();
  const narrow = useIsNarrow();
  const activeSection = useScrollSpy(SECTION_IDS);
  const colors = getColors(dark);

  const sectionStyle: CSSProperties = {
    minHeight: "var(--vh100)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingTop: mobile ? 76 : 64,
    // on mobile the pill nav is fixed over the bottom of every section, so the
    // content column has to clear it (plus the iOS home indicator)
    paddingBottom: mobile ? "calc(104px + env(safe-area-inset-bottom, 0px))" : 64,
    alignItems: mobile ? "center" : undefined,
    scrollSnapAlign: "start",
  };

  return (
    <div style={{
      background: colors.bg,
      color: colors.fg,
      fontFamily: "'0xProto', 'JetBrains Mono', monospace",
      transition: "background .35s ease, color .35s ease",
      minHeight: "var(--vh100)",
    }}>
      <DarkModeToggle dark={dark} onToggle={() => setDark(d => !d)} borderColor={colors.hair} color={colors.fg} />

      {mobile
        ? <MobileNav items={NAV_ITEMS} activeId={activeSection} colors={colors} />
        : <ContentsRail items={NAV_ITEMS} activeId={activeSection} colors={colors} />
      }

      <main style={{
        maxWidth: 720,
        margin: "0 auto",
        boxSizing: "content-box",
        // between 700px and 1140px the rail overlaps the centered column
        padding: mobile
          ? "0 calc(24px + env(safe-area-inset-right, 0px)) 0 calc(24px + env(safe-area-inset-left, 0px))"
          : narrow ? "0 176px 0 48px" : "0 48px",
      }}>
        <QuickLinksSection colors={colors} mobile={mobile} sectionStyle={sectionStyle} />
        <IntroductionSection colors={colors} mobile={mobile} sectionStyle={sectionStyle} />
        <SkillsSection colors={colors} mobile={mobile} sectionStyle={sectionStyle} />
        {SHOW_PROJECTS && <ProjectsSection colors={colors} mobile={mobile} sectionStyle={sectionStyle} />}
        <ContactSection colors={colors} mobile={mobile} sectionStyle={sectionStyle} />
      </main>
    </div>
  );
}
